'use client';

import type { IconName } from '@/components/entities/Sprite/Icon';
import type { ModalNameType } from '@/modals/components/ModalProvider';

import { PrivateLinks } from '@/components/shared/PrivateLinks';
import { useSidebarLink } from '@/hooks/useLinkHandler';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import NavigationItemContent from './components/NavigationItemContent';

const WRAPPER_BASE_CLASSES = 'group relative flex items-center gap-2 py-2.5 3xl:py-2.5 px-3 md:px-3 rounded-[3.125rem] paragraph-s-medium w-full min-w-0 text-left';
const WRAPPER_PRIMARY_COLOR_CLASSES = 'text-white sm:hover:text-c100';
const WRAPPER_ACCOUNT_COLOR_CLASSES = 'text-c100 sm:hover:text-white active:text-white';
const WRAPPER_TRANSITION_CLASSES = 'transition-opacity duration-200 md:hover:scale-[1.02]';
const WRAPPER_GRADIENT_CLASSES = 'before:content-[""] before:absolute before:inset-0 before:rounded-[3.125rem] before:bg-main-800 before:opacity-0 before:transition-opacity before:duration-200 before:pointer-events-none sm:hover:before:opacity-100 active:before:opacity-100';

interface NavigationItemProps {
  label: string;
  icon: IconName;
  href?: string;
  modal?: ModalNameType;
  onClick?: () => void;
  variant?: 'primary' | 'account';
  activeId?: string | null;
  setActiveId?: (id: string | null) => void;
  handleCloseSideBar?: () => void;
  isActive?: boolean;
  badge?: string;
}

const NavigationItem = ({
  label,
  icon,
  href,
  modal,
  onClick,
  variant = 'primary',
  setActiveId,
  handleCloseSideBar,
  isActive,
  badge,
}: NavigationItemProps) => {
  const handleLinkClick = useSidebarLink(handleCloseSideBar);

  const handleButtonClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (!modal) {
      return;
    }

    if (modal === 'support') {
      window.Intercom?.('show');
    } else {
      openModal(modal);
    }
  };

  if (href) {
    const prefetch = !href.includes('/profile');

    return (
      <PrivateLinks
        href={href}
        prefetch={prefetch}
        onClick={(e) => {
          handleLinkClick(e, href);
          setActiveId?.(label);
        }}
        className={cn(
          WRAPPER_BASE_CLASSES,
          variant === 'primary'
            ? WRAPPER_PRIMARY_COLOR_CLASSES
            : WRAPPER_ACCOUNT_COLOR_CLASSES,
          WRAPPER_TRANSITION_CLASSES,
          WRAPPER_GRADIENT_CLASSES,
          isActive && 'before:opacity-100',
          variant === 'account' && isActive && 'text-white',
        )}
      >
        <NavigationItemContent
          label={label}
          icon={icon}
          variant={variant}
          isActive={isActive}
          badge={badge}
        />
      </PrivateLinks>
    );
  }

  return (
    <button
      type="button"
      onClick={handleButtonClick}
      className={cn(
        WRAPPER_BASE_CLASSES,
        variant === 'primary'
          ? WRAPPER_PRIMARY_COLOR_CLASSES
          : WRAPPER_ACCOUNT_COLOR_CLASSES,
        WRAPPER_TRANSITION_CLASSES,
        WRAPPER_GRADIENT_CLASSES,
        isActive && 'before:opacity-100',
        variant === 'account' && isActive && 'text-white',
      )}
    >
      <NavigationItemContent
        label={label}
        icon={icon}
        variant={variant}
        isActive={isActive}
      />
    </button>
  );
};

export default NavigationItem;
